import { useState, useEffect, useCallback } from "react";
import RomaneioService from "../../services/resource/romaneioService";

const MESES = [
    "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
    "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

const DIAS_SEMANA = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

function gerarCalendario(ano, mes) {
    const primeiroDia = new Date(ano, mes - 1, 1);
    const ultimoDia = new Date(ano, mes, 0);

    const dias = [];

    const inicioSemana = primeiroDia.getDay(); // 0 (Dom) → 6 (Sáb)

    // espaços vazios antes do dia 1
    for (let i = 0; i < inicioSemana; i++) {
        dias.push(null);
    }

    // dias do mês
    for (let d = 1; d <= ultimoDia.getDate(); d++) {
        dias.push({ dia: d, mes, ano });
    }

    return dias;
}

export default function DiasTrabalhados({ ano: anoProp, mes: mesProp }) {
    const romaneioService = new RomaneioService();

    const [ano, setAno] = useState(anoProp);
    const [mes, setMes] = useState(mesProp);
    const [diasAtivos, setDiasAtivos] = useState(new Set());
    const [loading, setLoading] = useState(false);
    const [erro, setErro] = useState(null);

    const buscar = useCallback(async () => {
        setLoading(true);
        setErro(null);
        setDiasAtivos(new Set());
        try {
            const data = (await romaneioService.buscarDiasTrabalhados(anoProp, mesProp)).data;
            setDiasAtivos(new Set(data));
        } catch {
            setErro("Não foi possível carregar os dias trabalhados.");
        } finally {
            setLoading(false);
        }
    }, [ano, mes]);

    useEffect(() => {
        buscar();
    }, [buscar]);

    const irParaMesAnterior = () => {
        if (mes === 1) { setMes(12); setAno(ano - 1); }
        else setMes(mes - 1);
    };

    const irParaProximoMes = () => {
        if (mes === 12) { setMes(1); setAno(ano + 1); }
        else setMes(mes + 1);
    };

    const periodoLabel = `25/${String(mesProp - 1).padStart(2, "0")}/${anoProp} – 24/${String(mesProp).padStart(2, "0")}/${anoProp}`;

    const proximoMes = mes === 12 ? 1 : mes + 1;
    const proximoAno = mes === 12 ? ano + 1 : ano;

    return (
        <div className="p-3 text-foreground font-sans">
            <div className="grid grid-cols-2 gap-6">

                <div className="px-6">

                    {/* Navegação global */}
                    <div className="flex items-center justify-between mb-4">
                        <button onClick={irParaMesAnterior} className="px-3 py-1 border border-input rounded-md hover:bg-muted transition-colors duration-150">
                            ‹
                        </button>

                        <span className="font-semibold font-display">
                            {MESES[mes - 1]} / {MESES[proximoMes - 1]} {ano}
                        </span>

                        <button onClick={irParaProximoMes} className="px-3 py-1 border border-input rounded-md hover:bg-muted transition-colors duration-150">
                            ›
                        </button>
                    </div>

                    {/* DOIS MESES */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <CalendarioMes ano={ano} mes={mes} diasAtivos={diasAtivos} />
                        <CalendarioMes ano={proximoAno} mes={proximoMes} diasAtivos={diasAtivos} />
                    </div>

                </div>
                <div>


                    <div className="text-xs text-muted-foreground bg-muted rounded-lg p-3 mb-4 leading-relaxed">
                       Somente os dias destacados em verde são aqueles em que houve operação e será contado como dia trabalhado. Sábados e domingos não são contabilizados.
                    </div>

                    {/* Resumo */}
                    {!loading && !erro && (
                        <div className="flex justify-between bg-muted rounded-xl p-4 mt-4 text-center">
                            <div className="flex-1">
                                <div className="text-xl font-bold font-display text-foreground">{diasAtivos.size}</div>
                                <div className="text-xs text-muted-foreground">Dias de operação</div>
                            </div>

                            <div className="w-px bg-border mx-2" />

                            <div className="flex-1">
                                <div className="text-xl font-bold font-display text-foreground">Seg–Sex</div>
                                <div className="text-xs text-muted-foreground">Dias contabilizados</div>
                            </div>

                            <div className="w-px bg-border mx-2" />

                            <div className="flex-1">
                                <div className="text-sm font-bold text-foreground">
                                    {periodoLabel.split("–")[0].trim()}
                                </div>
                                <div className="text-xs text-muted-foreground">Início do período</div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function CalendarioMes({ ano, mes, diasAtivos }) {
    const calendario = gerarCalendario(ano, mes);

    const estaAtivo = ({ dia, mes: m, ano: a }) => {
        const chave = `${a}-${String(m).padStart(2, "0")}-${String(dia).padStart(2, "0")}`;
        return diasAtivos.has(chave);
    };

    const eFimDeSemana = (idx) => idx % 7 === 0 || idx % 7 === 6;

    return (
        <div>
            {/* Header */}
            <div className="text-center text-sm font-semibold font-display mb-2">
                {MESES[mes - 1]} {ano}
            </div>

            {/* Dias semana */}
            <div className="grid grid-cols-7 mb-1">
                {DIAS_SEMANA.map((d) => (
                    <div key={d} className="text-center text-[11px] font-semibold text-muted-foreground">
                        {d}
                    </div>
                ))}
            </div>

            {/* Grid */}
            <div className="grid grid-cols-7 gap-1">
                {calendario.map((celula, idx) => {
                    if (!celula) {
                        return <div key={idx} />; // espaço vazio
                    }

                    const ativo = estaAtivo(celula);
                    const fds = eFimDeSemana(idx);

                    const base = "relative aspect-square flex items-center justify-center rounded-lg text-sm transition-colors duration-150";

                    const estilo = ativo
                        ? `text-white font-semibold ${fds ? 'bg-orange-300' : 'bg-teal-700'}`
                        : fds
                            ? "bg-muted text-muted-foreground/50"
                            : celula.foraDoMes
                                ? "text-muted-foreground/50"
                                : "text-foreground";

                    return (
                        <div key={idx} className={`${base} ${estilo}`}>
                            {celula.dia}

                        </div>
                    );
                })}
            </div>
        </div>
    );
}